import { Icon } from 'Components/shared/Icon';
import React from 'react';
import { highlightText } from '~/lib/ui/autocomplete/utils';
import { Avatar, AvatarFallback } from '~/lib/ui/avatar';
import { cn } from '~/lib/utils';
import { TransferDirectDialOptionProps } from './index.types';

/**
 * Direct dial option shown when the search query matches a phone number pattern.
 *
 * Allows the user to transfer directly to the typed number without
 * selecting a contact from the results list.
 */
export const TransferDirectDialOption = ({
  onSelectDirectNumber,
  searchTerm,
  testID,
}: TransferDirectDialOptionProps): React.ReactElement => {
  const componentTestId = testID ? `${testID}-direct-dial` : 'direct-dial';
  const trimmedQuery = searchTerm.trim();

  return (
    <button
      type="button"
      onClick={onSelectDirectNumber}
      className={cn(
        'w-full flex items-center gap-[8px] px-[16px] py-[10px] rounded-md',
        'hover:bg-accent transition-colors cursor-pointer text-left'
      )}
      role="listitem"
      aria-label={`Transfer directly to ${trimmedQuery}`}
      data-testid={`${componentTestId}-direct-dial`}
    >
      <div
        className="flex items-center justify-center rounded-full flex-shrink-0"
        data-testid={`${componentTestId}-direct-dial-avatar`}
      >
        <Avatar className="bg-foreground size-[32px]">
          <AvatarFallback className="bg-foreground">
            <Icon icon="user" className="!text-white" />
          </AvatarFallback>
        </Avatar>
      </div>
      <div className="flex flex-col min-w-0">
        <span
          className="text-[16px] font-medium text-foreground leading-[22px]"
          data-testid={`${componentTestId}-direct-dial-number`}
        >
          {highlightText(trimmedQuery, trimmedQuery)}
        </span>
      </div>
    </button>
  );
};

TransferDirectDialOption.displayName = 'TransferDirectDialOption';
